import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { stylesApi } from '../services/api';
import { Style, CreateStyleData } from '../types';

const StylesManagement: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingStyle, setEditingStyle] = useState<Style | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const queryClient = useQueryClient();

  const { data: stylesData, isLoading } = useQuery({
    queryKey: ['styles', { page, search }],
    queryFn: () => stylesApi.getAll({ page, limit: 10, search: search || undefined }),
  });

  const { register, handleSubmit, reset, formState: { errors } } = useForm<CreateStyleData>();

  const createMutation = useMutation({
    mutationFn: stylesApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['styles'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      reset();
      setShowForm(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: CreateStyleData }) =>
      stylesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['styles'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      reset();
      setEditingStyle(null);
      setShowForm(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: stylesApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['styles'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
    },
  });

  const onSubmit = (data: CreateStyleData) => {
    if (editingStyle) {
      updateMutation.mutate({ id: editingStyle.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleEdit = (style: Style) => {
    setEditingStyle(style);
    reset({
      name: style.name,
      description: style.description || '',
    });
    setShowForm(true);
  };

  const handleDelete = (style: Style) => {
    if (window.confirm(`Are you sure you want to delete "${style.name}"?`)) {
      deleteMutation.mutate(style.id);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingStyle(null);
    reset();
  };

  return (
    <div className="styles-management">
      <div className="page-header">
        <h1>Dance Styles Management</h1>
        <button
          className="btn btn-primary"
          onClick={() => setShowForm(true)}
          disabled={showForm}
        >
          Add New Style
        </button>
      </div>

      {showForm && (
        <div className="form-container">
          <h2>{editingStyle ? 'Edit Style' : 'Create New Style'}</h2>
          <form onSubmit={handleSubmit(onSubmit)} className="admin-form">
            <div className="form-group">
              <label htmlFor="name">Name *</label>
              <input
                {...register('name', { required: 'Name is required' })}
                type="text"
                id="name"
                className={errors.name ? 'error' : ''}
              />
              {errors.name && <span className="error-text">{errors.name.message}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                {...register('description')}
                id="description"
                rows={3}
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                {editingStyle ? 'Update' : 'Create'} Style
              </button>
              <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="table-controls">
        <input
          type="text"
          placeholder="Search styles..."
          aria-label="Search styles"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="search-input"
        />
      </div>

      {isLoading ? (
        <div className="loading">Loading styles...</div>
      ) : stylesData && stylesData.data.length > 0 ? (
        <>
          <div className="table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Moves Count</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {stylesData.data.map((style) => (
                  <tr key={style.id}>
                    <td>
                      <strong>{style.name}</strong>
                    </td>
                    <td>{style.description || '-'}</td>
                    <td>{style.movesCount || 0}</td>
                    <td>{new Date(style.createdAt).toLocaleDateString()}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn btn-small btn-secondary"
                          onClick={() => handleEdit(style)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-small btn-danger"
                          onClick={() => handleDelete(style)}
                          disabled={deleteMutation.isPending}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {stylesData.pagination.pages > 1 && (
            <div className="pagination">
              {Array.from({ length: stylesData.pagination.pages }, (_, i) => i + 1).map(
                (pageNum) => (
                  <button
                    key={pageNum}
                    className={`btn btn-small ${
                      pageNum === page ? 'btn-primary' : 'btn-secondary'
                    }`}
                    onClick={() => setPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                )
              )}
            </div>
          )}
        </>
      ) : (
        <div className="empty-state">
          <p>No styles found. Create your first dance style!</p>
        </div>
      )}
    </div>
  );
};

export default StylesManagement;